import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQPage.css';

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('ordering');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const categories = [
    { id: 'ordering', name: 'Ordering', icon: '🛒' },
    { id: 'products', name: 'Products', icon: '👓' },
    { id: 'prescription', name: 'Prescription', icon: '📋' },
    { id: 'shipping', name: 'Shipping & Delivery', icon: '🚚' },
    { id: 'returns', name: 'Returns & Exchanges', icon: '🔄' },
    { id: 'payment', name: 'Payment', icon: '💳' },
    { id: 'warranty', name: 'Warranty', icon: '🛡️' },
    { id: 'contact', name: 'Contact Us', icon: '📞' }
  ];

  const faqData = {
    ordering: [
      {
        id: 'ord1',
        question: 'How do I place an order?',
        answer: 'Placing an order is easy! Simply browse our products, select the item you want, choose your preferred variant (color, size, lens type), and click "Add to Cart". Once you\'re ready, proceed to checkout, enter your shipping details, and complete the payment. You\'ll receive an order confirmation email shortly.'
      },
      {
        id: 'ord2',
        question: 'Can I modify or cancel my order after placing it?',
        answer: 'Orders can be modified or cancelled within 2 hours of placing them. Please contact our customer support immediately at support@visioncareoptics.com or call us at +91 98765 43210 with your order number.'
      },
      {
        id: 'ord3',
        question: 'Do I need a prescription to order eyeglasses?',
        answer: 'Yes, prescription eyeglasses require a valid prescription from an eye doctor. You can enter your prescription details during checkout. For non-prescription glasses like sunglasses or blue light blocking glasses, no prescription is needed.'
      },
      {
        id: 'ord4',
        question: 'How do I know my frame size?',
        answer: 'You can find your frame size by checking the inside of your current glasses\' temples or bridge. The numbers (e.g., 52-18-140) represent lens width, bridge width, and temple length. Our size guide can help you choose the right fit.'
      }
    ],
    products: [
      {
        id: 'prod1',
        question: 'Are your products authentic?',
        answer: 'Absolutely! We source all our products directly from authorized manufacturers and distributors. Every product comes with a certificate of authenticity and manufacturer\'s warranty where applicable.'
      },
      {
        id: 'prod2',
        question: 'What lens options do you offer?',
        answer: 'We offer a wide range of lens options including standard lenses, blue cut lenses (for digital eye strain), photochromic lenses (auto-darkening), progressive lenses (no-line multifocal), and high-index lenses (thinner and lighter for high prescriptions).'
      },
      {
        id: 'prod3',
        question: 'Do you offer polarized sunglasses?',
        answer: 'Yes, many of our sunglasses come with polarized lenses that reduce glare and provide better clarity. You can filter products by "Polarized" in our lens type filter.'
      },
      {
        id: 'prod4',
        question: 'What is blue light blocking technology?',
        answer: 'Blue light blocking lenses filter out harmful blue light emitted by digital screens, reducing eye strain, headaches, and improving sleep quality. They\'re ideal for people who spend long hours on computers or mobile devices.'
      }
    ],
    prescription: [
      {
        id: 'pres1',
        question: 'How do I enter my prescription?',
        answer: 'During checkout, you\'ll find a prescription form where you can enter your SPH, CYL, AXIS, ADD, and PD values for both eyes. You can find these numbers on your prescription from your eye doctor.'
      },
      {
        id: 'pres2',
        question: 'What do the prescription terms mean?',
        answer: '• SPH (Sphere): Indicates the lens power for nearsightedness (-) or farsightedness (+).\n• CYL (Cylinder): Corrects astigmatism.\n• AXIS: The orientation of astigmatism correction (0-180°).\n• ADD: Additional magnification for reading (for progressive/bifocal lenses).\n• PD (Pupillary Distance): Distance between your pupils.'
      },
      {
        id: 'pres3',
        question: 'Can I use an expired prescription?',
        answer: 'No, we require a valid prescription that is not older than 2 years. This ensures your safety and the accuracy of your lenses.'
      },
      {
        id: 'pres4',
        question: 'What if I enter my prescription incorrectly?',
        answer: 'If you realize you\'ve entered incorrect prescription details, please contact us immediately at support@visioncareoptics.com. We can modify your order within 2 hours of placement.'
      }
    ],
    shipping: [
      {
        id: 'ship1',
        question: 'How long does shipping take?',
        answer: 'Standard shipping takes 5-7 business days. Express shipping takes 2-3 business days. Prescription orders may take an additional 2-3 days for lens preparation.'
      },
      {
        id: 'ship2',
        question: 'Do you offer free shipping?',
        answer: 'Yes! We offer free standard shipping on all orders above ₹999. For orders below ₹999, a nominal shipping fee of ₹50 applies.'
      },
      {
        id: 'ship3',
        question: 'Do you ship internationally?',
        answer: 'Currently, we ship only within India. We\'re working on expanding our shipping destinations and will announce international shipping soon!'
      },
      {
        id: 'ship4',
        question: 'How can I track my order?',
        answer: 'Once your order ships, you\'ll receive a tracking number via email and SMS. You can also track your order from your account dashboard under "My Orders".'
      }
    ],
    returns: [
      {
        id: 'ret1',
        question: 'What is your return policy?',
        answer: 'We offer a 7-day return policy on all non-prescription products. Prescription products cannot be returned due to their custom nature. Items must be unused and in original packaging.'
      },
      {
        id: 'ret2',
        question: 'How do I initiate a return?',
        answer: 'Log in to your account, go to "My Orders", select the order you want to return, and click "Return Item". Follow the instructions to print the return label and ship the item back to us.'
      },
      {
        id: 'ret3',
        question: 'When will I get my refund?',
        answer: 'Once we receive and inspect the returned item, your refund will be processed within 5-7 business days. The amount will be credited to your original payment method.'
      },
      {
        id: 'ret4',
        question: 'Can I exchange an item for a different size?',
        answer: 'Yes, exchanges are possible within 7 days of delivery. Please initiate a return and place a new order for the correct size for faster processing.'
      }
    ],
    payment: [
      {
        id: 'pay1',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit/debit cards, UPI (Google Pay, PhonePe, Paytm), net banking, and Cash on Delivery (COD).'
      },
      {
        id: 'pay2',
        question: 'Is Cash on Delivery available?',
        answer: 'Yes, COD is available on orders below ₹10,000. A nominal fee of ₹30 may apply for COD orders.'
      },
      {
        id: 'pay3',
        question: 'Is it safe to use my credit card on your site?',
        answer: 'Absolutely! We use industry-standard SSL encryption to protect your payment information. We never store your card details on our servers.'
      },
      {
        id: 'pay4',
        question: 'Do you offer EMI options?',
        answer: 'Yes, EMI options are available on select credit cards for orders above ₹3,000. You can choose EMI at checkout.'
      }
    ],
    warranty: [
      {
        id: 'war1',
        question: 'What is your warranty policy?',
        answer: 'All frames come with a 1-year manufacturer\'s warranty against manufacturing defects. Lenses are covered for 6 months against coating defects.'
      },
      {
        id: 'war2',
        question: 'What does the warranty cover?',
        answer: 'The warranty covers frame defects like broken hinges, loose screws, and coating defects on lenses. It does not cover accidental damage, scratches, or normal wear and tear.'
      },
      {
        id: 'war3',
        question: 'How do I claim warranty?',
        answer: 'To claim warranty, please contact our customer support with your order number and photos of the defect. We\'ll guide you through the process.'
      }
    ],
    contact: [
      {
        id: 'con1',
        question: 'How can I contact customer support?',
        answer: 'You can reach us via:\n• Email: support@visioncareoptics.com\n• Phone: +91 98765 43210 (Mon-Sat, 10am-7pm)\n• Live Chat: Available on our website\n• Contact Form: Fill out the form on our Contact Us page'
      },
      {
        id: 'con2',
        question: 'What are your business hours?',
        answer: 'Our customer support is available Monday to Saturday, 10:00 AM to 7:00 PM IST. We\'re closed on Sundays and public holidays.'
      },
      {
        id: 'con3',
        question: 'Do you have a physical store?',
        answer: 'Yes, we have a flagship store at 123 Vision Street, New York, NY 10001. You can visit us to try frames and get expert advice from our optometrists.'
      }
    ]
  };

  return (
    <div className="faq-page">
      <div className="container">
        {/* Header */}
        <div className="faq-header">
          <h1 className="faq-title">
            <span className="title-line">Frequently Asked</span>
            <span className="title-line highlight">Questions</span>
          </h1>
          <p className="faq-subtitle">
            Find answers to common questions about ordering, products, prescriptions, and more
          </p>
        </div>

        {/* Search Bar */}
        <div className="faq-search">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input 
            type="text" 
            placeholder="Search for questions..." 
            className="faq-search-input"
          />
        </div>

        {/* Categories */}
        <div className="faq-categories">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="faq-content">
          <h2 className="category-title">
            {categories.find(c => c.id === activeCategory)?.name}
          </h2>
          
          <div className="faq-items">
            {faqData[activeCategory]?.map(item => (
              <div key={item.id} className="faq-item">
                <button
                  className={`faq-question ${openItems[item.id] ? 'open' : ''}`}
                  onClick={() => toggleItem(item.id)}
                >
                  <span className="question-text">{item.question}</span>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    className={`arrow-icon ${openItems[item.id] ? 'rotate' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                
                {openItems[item.id] && (
                  <div className="faq-answer">
                    {item.answer.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Still Need Help */}
        <div className="help-section">
          <h2>Still Need Help?</h2>
          <p>Can't find the answer you're looking for? We're here to help!</p>
          
          <div className="help-options">
            <Link to="/contact" className="help-card">
              <div className="help-icon">📧</div>
              <h3>Contact Us</h3>
              <p>Send us a message and we'll get back to you within 24 hours</p>
            </Link>
            
            <a href="tel:+919876543210" className="help-card">
              <div className="help-icon">📞</div>
              <h3>Call Us</h3>
              <p>Mon-Sat, 10am-7pm<br/>+91 9443653501</p>
            </a>
            
            <div className="help-card">
              <div className="help-icon">💬</div>
              <h3>Live Chat</h3>
              <p>Chat with our support team for instant answers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;